<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
} from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ useScope: "global" });
const $t = t;

const props = defineProps({
  categories: { type: Object, required: true },
  selectedCategory: { type: String, required: true },
  addToCart: { type: Function, required: true },
  labels: {
    type: Object,
    default: () => ({
      appetizers: "Appetizers",
      homestyle: "Homestyle",
      steak: "Steak",
      chicken: "Chicken",
      beverages: "Beverages",
    }),
  },
  // IMPORTANT: camelCase prop that matches :is-rtl on the parent
  isRtl: { type: Boolean, default: false },
});

const emit = defineEmits(["update:selectedCategory"]);

const rtl = computed(() => props.isRtl || document?.dir === "rtl");

const tabList = computed(() =>
  Object.keys(props.categories).map((key) => ({
    key,
    label: props.labels?.[key] ?? key,
  }))
);
const items = computed(() => props.categories[props.selectedCategory] ?? []);

const tabsWrap = ref(null);
const tabRefs = ref([]);
const cardsRow = ref(null);

const dragging = ref(false);
const approxIndex = ref(0);

const tabClass = (key) => [
  "border-[var(--chip-border)] text-[var(--chip-text)] bg-transparent",
  "hover:border-[var(--primary)] hover:text-[var(--on-primary)] hover:bg-[var(--primary)]",
  props.selectedCategory === key
    ? "is-active border-[var(--primary)] text-[var(--on-primary)] bg-[var(--chip-bg-active)] shadow-[var(--glow-sm)]"
    : "",
];

const setCategory = async (key) => {
  emit("update:selectedCategory", key);
  await nextTick();
  const active =
    tabRefs.value.find((el) => el?.classList.contains("is-active")) ||
    tabRefs.value[0];
  if (!active || !tabsWrap.value) return;
  const wrap = tabsWrap.value;
  const rect = active.getBoundingClientRect();
  const wrapRect = wrap.getBoundingClientRect();
  const delta =
    rect.left - (wrapRect.left + wrapRect.width / 2 - rect.width / 2);
  wrap.scrollBy({ left: delta, behavior: "smooth" });
};

/* drag-to-scroll */
function setupDragScroll(el) {
  if (!el) return;
  let isDown = false,
    startX = 0,
    startScroll = 0;
  const onDown = (e) => {
    isDown = true;
    dragging.value = true;
    startX = e.touches ? e.touches[0].pageX : e.pageX;
    startScroll = el.scrollLeft;
  };
  const onMove = (e) => {
    if (!isDown) return;
    const x = e.touches ? e.touches[0].pageX : e.pageX;
    el.scrollLeft = startScroll - (x - startX);
    if (e.cancelable) e.preventDefault();
  };
  const onUp = () => {
    isDown = false;
    dragging.value = false;
  };
  el.addEventListener("mousedown", onDown);
  el.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
  el.addEventListener("touchstart", onDown, { passive: false });
  el.addEventListener("touchmove", onMove, { passive: false });
  el.addEventListener("touchend", onUp);
  el.addEventListener("touchcancel", onUp);
  return () => {
    el.removeEventListener("mousedown", onDown);
    el.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    el.removeEventListener("touchstart", onDown);
    el.removeEventListener("touchmove", onMove);
    el.removeEventListener("touchend", onUp);
    el.removeEventListener("touchcancel", onUp);
  };
}

let cleanTabs, cleanCards;

onMounted(() => {
  if (cardsRow.value) {
    const handler = () => {
      const w = cardsRow.value.clientWidth || 1;
      approxIndex.value = Math.round(cardsRow.value.scrollLeft / (w * 0.82));
    };
    cardsRow.value.addEventListener("scroll", handler, { passive: true });
  }
  if (rtl.value && tabsWrap.value) {
    tabsWrap.value.scrollLeft = tabsWrap.value.scrollWidth;
  }
  cleanTabs = setupDragScroll(tabsWrap.value);
  cleanCards = setupDragScroll(cardsRow.value);
});

onBeforeUnmount(() => {
  cleanTabs && cleanTabs();
  cleanCards && cleanCards();
});

watch(
  () => props.selectedCategory,
  () => nextTick().then(() => setCategory(props.selectedCategory))
);
</script>

<template>
  <!-- dir + explicit alignment -->
  <section class="md:hidden" :dir="rtl ? 'rtl' : 'ltr'">
    <!-- Tabs -->
<div class="sticky top-14 z-30 bg-[var(--bg)]/80 backdrop-blur border-b border-[var(--border)]">
  <div
    ref="tabsWrap"
    class="overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth select-none px-4"  
    :class="dragging ? 'cursor-grabbing' : 'cursor-grab'"
  >
    <div class="flex gap-2 w-max py-3" :class="rtl ? 'justify-end' : 'justify-start'">
      <button
        v-for="tab in tabList"
        :key="tab.key"
        @click="setCategory(tab.key)"
        class="relative snap-start rounded-full border transition cursor-pointer px-5 py-2
               first:ms-4 last:me-4"  
        :class="tabClass(tab.key)"
        ref="tabRefs"
      >
        <span class="whitespace-nowrap text-sm font-medium">{{ tab.label }}</span>
        <span v-if="selectedCategory === tab.key"
              class="absolute left-3 right-3 -bottom-1 h-[2px] bg-[var(--primary)] rounded-full"/>
      </button>
    </div>
  </div>
</div>


    <!-- Cards -->
    <div class="mt-4">
      <div
        ref="cardsRow"
        class="flex gap-4 overflow-x-auto px-4 no-scrollbar snap-x snap-mandatory scroll-smooth select-none"
        :class="dragging ? 'cursor-grabbing' : 'cursor-grab'"
        :dir="rtl ? 'rtl' : 'ltr'"
      >
        <article
          v-for="(item, i) in items"
          :key="i"
          class="snap-center shrink-0 w-[82%] sm:w-[70%] max-w-[380px] bg-[var(--surface)] rounded-2xl shadow-md overflow-hidden border border-[var(--border)]"
        >
          <img
            :src="item.image"
            :alt="$t(item.titleKey)"
            class="h-44 w-full object-cover"
          />
          <!-- RIGHT in Arabic, LEFT in English -->
          <!-- RIGHT in Arabic (no right padding), LEFT in English (no left padding) -->
          <div
            class="py-4 flex flex-col gap-2"
            :style="
              rtl
                ? {
                    textAlign: 'right',
                    paddingInlineStart: '1rem',
                    paddingInlineEnd: '0',
                  } // Arabic
                : {
                    textAlign: 'left',
                    paddingInlineStart: '1rem',
                    paddingInlineEnd: '0',
                  } // English
            "
          >
            <h4 class="text-lg font-semibold text-[var(--primary)]">
              {{ $t(item.titleKey) }}
            </h4>
            <p class="text-sm text-[var(--on-surface)]/80 line-clamp-3">
              {{ $t(item.descriptionKey) }}
            </p>
            <span class="text-base font-bold text-[var(--primary)] block">
              {{ $t(item.priceKey) }}
            </span>
          </div>
        </article>
      </div>

      <!-- Dots -->
      <div class="flex justify-center gap-2 mt-4">
        <span
          v-for="(item, i) in items"
          :key="i"
          class="w-2 h-2 rounded-full transition"
          :class="
            i === approxIndex ? 'bg-[var(--primary)]' : 'bg-[var(--border)]'
          "
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
